import React, { useState, useRef } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";
import { Section, Grid, Card } from "../../components/layout";
import { LAYOUT } from "../../constants/layout";
import { useLayout } from "../../contexts/LayoutContext";
import logger from '../../utils/logger';
import { conversations as initialConversations, channels as initialChannels, currentUser } from "../../data/messagesData";

const Messages = () => {
  const { headerHeight } = useLayout();

  const [conversations, setConversations] = useState(initialConversations);
  const [channels, setChannels] = useState(initialChannels.map(ch => ({ ...ch, selected: false })));
  const [selectedChat, setSelectedChat] = useState(conversations[0]);
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [filters, setFilters] = useState({
    clients: true,
    prospects: true,
    team: true,
  });
  const [showChannels, setShowChannels] = useState(true);
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [showAllConversations, setShowAllConversations] = useState(false);
  const MAX_VISIBLE = 5;

  const toggleStar = (convId, e) => {
    e.stopPropagation();
    setConversations((prev) =>
      prev.map((c) =>
        c.id === convId ? { ...c, starred: !c.starred } : c
      )
    );
  };

  const handleChannelClick = (channelId) => {
    setChannels((prev) =>
      prev.map((ch) =>
        ch.id === channelId ? { ...ch, selected: !ch.selected } : ch
      )
    );
  };

  const handleNewConversation = () => {
    logger.info("Opening new conversation modal");
  };

  const handleAttachment = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setAttachments((prev) => [...prev, ...files]);
      e.target.value = "";
    }
  };

  const removeAttachment = (i) =>
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const toggleEmojiPicker = () => setShowEmojiPicker((v) => !v);

  const handleSendMessage = () => {
    if (message.trim()) {
      logger.info("Sending message:", message);
      setMessage("");
    }
  };

  const createTask = (content) => {
    logger.info("Creating task from message:", content);
  };

  const filteredConversations = conversations.filter((conv) => {
    if (conv.type === "client" && !filters.clients) return false;
    if (conv.type === "prospect" && !filters.prospects) return false;
    if (conv.type === "team" && !filters.team) return false;
    return true;
  });

  const visibleConversations = showAllConversations
    ? filteredConversations
    : filteredConversations.slice(0, MAX_VISIBLE);
  const hasMore = filteredConversations.length > MAX_VISIBLE;

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Messages", path: "/advisor-portal/messages" },
      ]}
      title="Messages"
      subtitle="Manage your client communications"
      actionButton={
        <button
          onClick={handleNewConversation}
          className="px-3 py-1.5 bg-[#029831] dark:bg-[#027e2d] text-white text-[10px] rounded-lg hover:bg-[#018529] transition-colors flex items-center"
        >
          <svg
            className="w-3 h-3 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          New Message
        </button>
      }
    >
      {/* OUTER FLEX WRAPPER */}
      <div className="flex flex-1 min-h-0 bg-surface-default dark:bg-surface-default overflow-hidden">
        {/* LEFT SIDEBAR */}
        <Card
          padding={false}
          className="w-[240px] border-r border-gray-200 dark:border-gray-700 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-none min-h-0"
        >
          {/* Filters */}
          <div className="p-1.5 border-b border-gray-200 dark:border-gray-700 bg-surface-default dark:bg-surface-default">
            <Grid columns={3} className="mb-1.5">
              {['clients', 'prospects', 'team'].map((key, i) => (
                <label key={i} className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={filters[key]}
                    onChange={() =>
                      setFilters((f) => ({ ...f, [key]: !f[key] }))
                    }
                    className="form-checkbox h-3 w-3 text-blue-500"
                  />
                  <span className="ml-1 text-[10px] text-gray-700 dark:text-gray-300 capitalize">
                    {key}
                  </span>
                </label>
              ))}
            </Grid>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-6 pr-2 py-1 text-[10px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <svg
                className="w-3 h-3 absolute left-2 top-1.5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Conversations Container */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Conversations List */}
            <div className="overflow-y-auto flex-1 min-h-0">
              <Section className="py-1" spacing={false}>
                <div className="px-2 mb-0.5 flex items-center justify-between">
                  <h2 className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Conversations
                  </h2>
                  <button
                    onClick={handleNewConversation}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                  >
                    <svg
                      className="w-3 h-3 text-gray-500 dark:text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                </div>
                {visibleConversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedChat(conv)}
                    className={`px-2 py-1 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                      selectedChat.id === conv.id
                        ? "bg-blue-50 dark:bg-blue-900 border-l-2 border-blue-500"
                        : "border-l-2 border-transparent"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {conv.isGroup ? (
                        <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-4 h-4 text-gray-500 dark:text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 12.75c1.63 0 3.07.39 4.24.9..." />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0">
                          {conv.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className="text-[10px] font-medium text-gray-900 dark:text-gray-100 truncate">
                            {conv.isGroup ? conv.title : conv.name}
                          </p>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={(e) => toggleStar(conv.id, e)}
                              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                            >
                              <svg
                                className={`w-3 h-3 ${conv.starred ? "text-yellow-400" : "text-gray-400"}`}
                                fill={conv.starred ? "currentColor" : "none"}
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path d="M11.049 2.927c.3-.921..." />
                              </svg>
                            </button>
                            <span className="text-[8px] text-gray-500 dark:text-gray-500 whitespace-nowrap">
                              {conv.time}
                            </span>
                          </div>
                        </div>
                        {conv.company && (
                          <p className="text-[8px] text-gray-500 dark:text-gray-500 truncate">
                            {conv.company}
                          </p>
                        )}
                        {conv.isGroup && (
                          <p className="text-[8px] text-gray-500 dark:text-gray-500 truncate">
                            {conv.participants.join(", ")}
                          </p>
                        )}
                        {conv.lastMessage && (
                          <p className="text-[8px] text-gray-500 dark:text-gray-500 truncate">
                            {conv.lastMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Section>
            </div>

            {/* Channels (sticky to bottom) */}
            <div className="border-t border-gray-200 dark:border-gray-700">
              <Section className="py-1" spacing={false}>
                <div
                  className="px-2 mb-0.5 flex items-center justify-between cursor-pointer"
                  onClick={() => setShowChannels((v) => !v)}
                >
                  <h2 className="text-[10px] font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Channels
                  </h2>
                  <svg
                    className={`w-3 h-3 text-gray-500 dark:text-gray-500 transform transition-transform ${
                      showChannels ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showChannels &&
                  channels.map((ch) => (
                    <div
                      key={ch.id}
                      onClick={() => !ch.locked && handleChannelClick(ch.id)}
                      className={`px-2 py-1 flex items-center space-x-1.5 cursor-pointer transition-colors ${
                        ch.locked
                          ? "cursor-not-allowed opacity-60"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800"
                      } ${ch.selected ? "bg-blue-50 dark:bg-blue-900" : ""}`}
                    >
                      {ch.locked ? (
                        <svg
                          className="w-3 h-3 text-gray-400 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M18 8h-1V6c0-2.76..." />
                        </svg>
                      ) : (
                        <svg
                          className="w-3 h-3 text-gray-400 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2C6.48 2 2 6.48..." />
                        </svg>
                      )}
                      <span className="text-[10px] text-gray-700 dark:text-gray-300 truncate">
                        {ch.name}
                      </span>
                    </div>
                  ))}
              </Section>
            </div>
          </div>
        </Card>

        {/* CHAT MAIN AREA */}
        <div className="flex-1 flex flex-col min-h-0">
          {selectedChat ? (
            <>
              {/* Header */}
              <Card
                padding={false}
                className="border-b border-gray-200 dark:border-gray-700 bg-surface-default dark:bg-surface-default shadow-sm rounded-none"
              >
                <Section className="flex items-center justify-between" spacing={false}>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium shadow-sm flex-shrink-0">
                      {selectedChat.name ? selectedChat.name.charAt(0) : "#"}
                    </div>
                    <div>
                      <h2 className="text-base font-medium text-gray-900 dark:text-gray-100">
                        {selectedChat.isGroup ? selectedChat.title : selectedChat.name}
                      </h2>
                      {selectedChat.company && (
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {selectedChat.company}
                        </p>
                      )}
                    </div>
                  </div>
                  <Grid columns={3} gap="0.5rem" className="items-center">
                    <button title="Create Task" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" onClick={() => createTask(selectedChat)}>
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12..." />
                      </svg>
                    </button>
                    <button title="View Files" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21 6h-8V4c0-1.1..." />
                      </svg>
                    </button>
                    <button title="More Options" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9..." />
                      </svg>
                    </button>
                  </Grid>
                </Section>
              </Card>

              {/* MESSAGES */}
              <Section className="flex-1 overflow-y-auto min-h-0 bg-gray-50 dark:bg-gray-900 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                <div className="flex justify-center mt-4">
                  <Card className="text-xs text-gray-500 dark:text-gray-500 px-3 py-1 rounded-full shadow-sm">
                    {currentUser.joinedDate}
                  </Card>
                </div>
                <div className="flex justify-center my-3">
                  <Card className="text-sm text-gray-900 dark:text-gray-100 px-3 py-1.5 rounded-lg shadow-sm">
                    You joined
                  </Card>
                </div>
                <div className="flex justify-end mb-3 px-4">
                  <div className="bg-blue-500 text-white text-sm rounded-lg px-3 py-1.5 shadow-sm max-w-lg">
                    Hi!
                  </div>
                </div>
              </Section>

              {/* INPUT */}
              <Card padding={false} className="border-t border-gray-200 dark:border-gray-700 bg-surface-default dark:bg-surface-default rounded-none">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                />
                {attachments.length > 0 && (
                  <Grid columns={4} gap="0.5rem" className="mb-2 px-4 pt-4">
                    {attachments.map((file, idx) => (
                      <Card key={idx} className="flex items-center bg-gray-100 dark:bg-gray-800 px-3 py-1 text-sm">
                        <span className="truncate max-w-xs text-gray-900 dark:text-gray-100">{file.name}</span>
                        <button onClick={() => removeAttachment(idx)} className="ml-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </Card>
                    ))}
                  </Grid>
                )}
                <Grid columns={12} gap="0.75rem" className="items-center px-4 py-3">
                  <div className="col-span-1">
                    <button onClick={handleAttachment} title="Add Attachment" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15.172 7l-6.586 6.586..." />
                      </svg>
                    </button>
                  </div>
                  <div className="col-span-10 relative">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      placeholder="Type a message"
                      className="w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button onClick={toggleEmojiPicker} title="Add Emoji" className="absolute right-3 top-2">
                      <svg className="w-4 h-4 text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.99 2C6.47 2 2 6.48..." />
                      </svg>
                    </button>
                    {showEmojiPicker && (
                      <Card className="absolute bottom-full right-0 mb-2 bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none border border-gray-200 dark:border-gray-700">
                        <div className="p-2">
                          <span className="text-sm text-gray-500 dark:text-gray-500">
                            Emoji picker coming soon!
                          </span>
                        </div>
                      </Card>
                    )}
                  </div>
                  <div className="col-span-1">
                    <button onClick={handleSendMessage} title="Send Message" className="p-2 bg-blue-500 hover:bg-blue-600 transition-colors rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                  </div>
                </Grid>
              </Card>
            </>
          ) : (
            <Section className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418..." />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  No conversation selected
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
                  Choose a conversation to start messaging
                </p>
              </div>
            </Section>
          )}
        </div>
      </div>
    </SubPageLayout>
  );
};

export default Messages;
